import { Editor } from "@toast-ui/react-editor";
import "@toast-ui/editor/dist/toastui-editor.css";
import "@toast-ui/editor/dist/i18n/ko-kr";
import { useEffect, useRef, useState } from "react";
import {
  Link,
  useLocation,
  useNavigate,
  useBeforeUnload,
} from "react-router-dom";
import {
  insertPost,
  selectDetailPost,
  updatePost,
  deletePost,
} from "../service/PostApiList";
import Progress from "./Progress";
import { Box, Grid, Stack, TextField, Button, Typography } from "@mui/material";
import { LoadingButton } from "@mui/lab";

const API_BASE = process.env.REACT_APP_APIURL;

const PostEdit = () => {
  const navigate = useNavigate();
  const admin = sessionStorage.getItem("admin");

  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(true);
  const location = useLocation();
  const prevParentnode = location.state?.parentnode ?? null;
  const prevID = location.state?.id ?? null;
  const isNew = location.state?.isNew ?? false;

  const editorRef = useRef();

  // 🔹 썸네일 상태
  const [thumbnail, setThumbnail] = useState({
    file: null,
    preview: null,
    isObjectUrl: false, // 서버 URL은 revoke하지 않기 위해 구분
  });

  // 서버 썸네일 URL 만들기
  const thumbUrl = (id, w = 520, fmt = "webp", ver) => {
    const base = `${API_BASE}/posts/${id}/thumbnail?w=${w}&fmt=${fmt}`;
    return ver ? `${base}&v=${ver}` : base;
  };
  const fileInputRef = useRef(null);

  // 🔹 본문 내 이미지 objectURL을 모아두는 저장소 (누수 방지용)
  const editorObjectUrlsRef = useRef([]);

  // 🔹 폼 변경 여부 추적 (페이지 이탈 경고용)
  const [dirty, setDirty] = useState(false);

  const [formData, setFormData] = useState({
    id: prevID,
    title: "",
    parentnode: prevParentnode,
    category: "P",
    content: null,
    regdate: null,
    ispublic: false,
    thumbnail: null, // 업로드 시 사용할 수 있게 보관
  });

  const CheckAccess = () => {
    if (!(prevID || prevParentnode)) {
      alert("정상적인 접근이 아닙니다. 이전 페이지로 이동합니다.");
      navigate(-1);
    } else if (typeof admin === "undefined" || admin === null || admin === "") {
      alert("관리자 모드가 아닙니다. 이전 페이지로 이동합니다.");
      navigate(-1);
    }
  };

  const revokeAllEditorObjectUrls = () => {
    for (const url of editorObjectUrlsRef.current) {
      try {
        URL.revokeObjectURL(url);
      } catch (_) {}
    }
    editorObjectUrlsRef.current = [];
  };

  const insertPostToAPI = async (e) => {
    e.preventDefault();
    setLoading(true);

    // const fd = new FormData();
    // fd.append("title", formData.title);
    // fd.append("parentnode", String(formData.parentnode));
    // fd.append("category", formData.category);
    // fd.append("content", formData.content ?? "");
    // fd.append("ispublic", String(formData.ispublic));

    // if (thumbnail.file) {
    //   fd.append("thumbnail", thumbnail.file);
    // }

    try {
      const payload = {
        title: formData.title,
        parentnode: formData.parentnode, // 숫자 or 숫자 문자열
        category: formData.category,
        content: formData.content ?? "",
        ispublic: formData.ispublic, // ← PostApiList는 ispublic 키를 기대
        thumbnail: thumbnail.file || null, // 파일 또는 null
      };

      const result = await insertPost(payload); // ← 객체를 넘긴다 (FormData 아님)
      // const result = await insertPost(fd);

      // 저장 성공 시, 에디터 objectURL 정리
      revokeAllEditorObjectUrls();

      setLoading(false);
      if (formData.category === "C") {
        navigate("/posts/" + formData.parentnode);
      } else {
        navigate("/post/" + result.data.id);
      }
    } catch (err) {
      setLoading(false);
      alert("저장 중 오류가 발생했습니다.");
      console.error(err);
    }
  };

  const updatePostToAPI = async (e) => {
    e.preventDefault();
    setLoading(true);

    // const fd = new FormData();
    // fd.append("id", String(formData.id));
    // fd.append("title", formData.title);
    // fd.append("parentnode", String(formData.parentnode));
    // fd.append("category", formData.category);
    // fd.append("content", formData.content ?? "");
    // fd.append("ispublic", String(formData.ispublic));

    // if (thumbnail.file) {
    //   fd.append("thumbnail", thumbnail.file);
    // }

    try {
      const payload = {
        id: formData.id, // 숫자 or 숫자 문자열
        title: formData.title,
        parentnode: formData.parentnode,
        category: formData.category,
        content: formData.content ?? "",
        ispublic: formData.ispublic,
        thumbnail: thumbnail.file || null,
      };

      await updatePost(payload); // ← 객체를 넘긴다
      // await updatePost(fd);

      // 저장 성공 시, 에디터 objectURL 정리
      revokeAllEditorObjectUrls();

      setLoading(false);
      if (formData.category === "C") {
        navigate("/posts/" + formData.parentnode);
      } else {
        navigate("/post/" + formData.id);
      }
    } catch (err) {
      setLoading(false);
      alert("수정 중 오류가 발생했습니다.");
      console.error(err);
    }
  };

  const fn1 = async () => {
    CheckAccess();
    if (prevID) {
      const result = await selectDetailPost(prevID);
      const temp = result.data;

      if (temp.category === "P") {
        editorRef.current?.getInstance?.().setMarkdown(temp.content || "");
      }

      setFormData((prev) => ({
        ...prev,
        title: temp.title,
        parentnode: temp.parentnode,
        category: temp.category,
        content: temp.content,
        regdate: new Date(temp.regdate).toLocaleDateString(),
        ispublic: temp.ispublic,
        // thumbnailUrl이 있다면 아래처럼 미리보기 세팅 가능
        // thumbnail: null 은 유지하고, preview에만 URL 세팅
      }));

      // ✅ 서버에 저장된 썸네일을 미리보기로 세팅 (존재할 때만)
      // 캐시 버전은 regdate로 고정(있으면), 없어도 동작
      const ver = temp.regdate ? new Date(temp.regdate).getTime() : 0;
      const url = thumbUrl(prevID, 520, "webp", ver);
      const img = new Image();
      img.onload = () => {
        setThumbnail({ file: null, preview: url, isObjectUrl: false });
      };
      img.onerror = () => {
        // 썸네일이 없으면 미리보기 표시하지 않음(404 등)
        setThumbnail({ file: null, preview: null, isObjectUrl: false });
      };
      img.src = url;
    }
    setProgress(false);
  };

  useEffect(() => {
    fn1();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    console.log(formData);
    console.log(isNew);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData]);

  // 저장/삭제 등 진행 중엔 이탈 경고를 띄우지 않고,
  // 실제로 수정(dirty)된 경우에만 안내.
  useBeforeUnload((event) => {
    if (loading) return;
    if (!dirty) return;
    const message =
      "정말로 페이지를 벗어나시겠습니까? 작성 중인 내용이 저장되지 않을 수 있습니다.";
    event.preventDefault();
    event.returnValue = message;
    return message;
  });

  // 썸네일 미리보기 URL 정리
  useEffect(() => {
    return () => {
      // 이전 미리보기가 objectURL일 때만 revoke
      if (thumbnail.preview && thumbnail.isObjectUrl) {
        try {
          URL.revokeObjectURL(thumbnail.preview);
        } catch (_) {}
      }
      // 컴포넌트 언마운트 시 에디터 내 objectURL도 정리
      revokeAllEditorObjectUrls();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 썸네일 파일이 바뀌면 formData.thumbnail도 동기화 + dirty 표시
  useEffect(() => {
    setFormData((prev) => ({ ...prev, thumbnail: thumbnail.file || null }));
    if (thumbnail.file !== null) setDirty(true);
  }, [thumbnail.file]);

  const handleChangeForm = (e) => {
    const { value, name } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setDirty(true);
  };

  const handleChangeEditor = () => {
    const editorContent = editorRef.current.getInstance().getMarkdown();
    setFormData((prev) => ({ ...prev, content: editorContent }));
    setDirty(true);
  };

  const clickDelete = async () => {
    if (
      window.confirm(
        (formData.category === "P"
          ? "게시글을"
          : "이 카테고리를 삭제하면 하위 카테고리/게시글도 모두 삭제됩니다. 그래도") +
          " 삭제 하시겠습니까?"
      )
    ) {
      setLoading(true);
      try {
        await deletePost(prevID);
        // 삭제 성공 시에도 objectURL 정리
        revokeAllEditorObjectUrls();
        navigate("/posts/" + prevParentnode);
        alert("삭제되었습니다.");
      } catch (e) {
        alert("삭제되지 않았습니다 : " + e);
        setLoading(false);
      }
    }
  };

  // ==========================
  // 썸네일 UI/핸들러
  // ==========================
  const handlePickThumbnail = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // 이미 생성된 미리보기 URL이 있으면 해제
    if (thumbnail.preview) {
      try {
        URL.revokeObjectURL(thumbnail.preview);
      } catch (_) {}
    }

    const url = URL.createObjectURL(file);
    setThumbnail({ file, preview: url, isObjectUrl: true });
  };

  const handleRemoveThumbnail = () => {
    if (thumbnail.preview && thumbnail.isObjectUrl) {
      try {
        URL.revokeObjectURL(thumbnail.preview);
      } catch (_) {}
    }
    setThumbnail({ file: null, preview: null, isObjectUrl: false });
    if (fileInputRef.current) fileInputRef.current.value = "";
    setDirty(true);
  };

  return (
    <>
      <Progress progress={progress} />
      <div id="content">
        <Box sx={{ mt: 3, mb: 3 }}>
          <Link to={"/posts/" + formData.parentnode}>상위 카테고리로 이동</Link>
        </Box>

        <form onSubmit={isNew ? insertPostToAPI : updatePostToAPI}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                name="title"
                placeholder="게시글/카테고리 제목을 입력해 주세요."
                value={formData.title}
                onChange={handleChangeForm}
                label="제목"
              />
              <Button
                variant={formData.ispublic ? "contained" : "outlined"}
                onClick={() => {
                  setFormData((prev) => ({
                    ...prev,
                    ispublic: !prev.ispublic,
                  }));
                  setDirty(true);
                }}
                sx={{ ml: 1 }}
              >
                {formData.ispublic ? "공개 중" : "비공개 중"}
              </Button>
            </Grid>

            <Grid item xs={12}>
              <input
                name="category"
                type="radio"
                value={"C"}
                id="categoryCheck"
                checked={formData.category === "C"}
                onChange={handleChangeForm}
                disabled={!isNew}
              />
              <label htmlFor="categoryCheck">카테고리</label>
              <input
                name="category"
                type="radio"
                value={"P"}
                id="postCheck"
                checked={formData.category === "P"}
                onChange={handleChangeForm}
                disabled={!isNew}
              />
              <label htmlFor="postCheck">게시글</label>
            </Grid>

            {/* ==========================
                썸네일 UI 블록
               ========================== */}
            {formData.category === "C" ? (
              ""
            ) : (
              <Grid item xs={12}>
                {/* 1번 박스: 미리보기 (썸네일 선택 시에만 표시) */}
                {thumbnail.preview && (
                  <Box
                    sx={{
                      width: 520,
                      height: 520,
                      border: "4px solid #000",
                      mx: "auto",
                      mb: 3,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      overflow: "hidden",
                      borderRadius: "4px",
                    }}
                  >
                    <img
                      src={thumbnail.preview}
                      alt="썸네일 미리보기"
                      style={{
                        maxWidth: "100%",
                        maxHeight: "100%",
                        objectFit: "contain",
                      }}
                    />
                  </Box>
                )}

                {/* 2번/3번 박스: 추가/삭제 버튼 */}
                <Grid
                  container
                  spacing={3}
                  justifyContent="center"
                  alignItems="center"
                >
                  <Grid item>
                    {/* 2번 박스 */}
                    <Button
                      variant="outlined"
                      onClick={handlePickThumbnail}
                      sx={{
                        width: 240,
                        height: 100,
                        border: "4px solid #000",
                        borderRadius: "4px",
                      }}
                    >
                      <Typography>2번 박스</Typography>
                      <Typography sx={{ ml: 1 }}>썸네일 추가</Typography>
                    </Button>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      hidden
                      onChange={handleFileChange}
                    />
                  </Grid>

                  <Grid item>
                    {/* 3번 박스 */}
                    <Button
                      variant="outlined"
                      onClick={handleRemoveThumbnail}
                      disabled={!thumbnail.preview}
                      sx={{
                        width: 240,
                        height: 100,
                        border: "4px solid #000",
                        borderRadius: "4px",
                      }}
                    >
                      <Typography>3번 박스</Typography>
                      <Typography sx={{ ml: 1 }}>썸네일 삭제</Typography>
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
            )}

            {/* 게시글 본문 에디터 */}
            {formData.category === "C" ? (
              ""
            ) : (
              <Grid item xs={12}>
                <Editor
                  initialValue={formData.content || ""}
                  previewStyle="vertical"
                  height="600px"
                  initialEditType="wysiwyg"
                  useCommandShortcut={false}
                  language="ko-KR"
                  ref={editorRef}
                  onChange={handleChangeEditor}
                  hooks={{
                    addImageBlobHook: async (blob, callback) => {
                      try {
                        // 임시 미리보기 URL 생성
                        const imageUrl = URL.createObjectURL(blob);
                        editorObjectUrlsRef.current.push(imageUrl);

                        // 안전한 기본 alt 텍스트 (blob.name을 직접 변경하지 않음)
                        const alt = `image-${Date.now()}`;

                        // 본문 삽입
                        callback(imageUrl, alt);

                        // 기본 업로드 동작 차단
                        return false;
                      } catch (err) {
                        console.error(err);
                        alert("이미지를 첨부하는 중 오류가 발생했습니다.");
                        return false;
                      }
                    },
                  }}
                />
              </Grid>
            )}

            <Grid item xs={12} sx={{ mb: 2 }}>
              <Stack spacing={2} direction="row">
                {isNew ? (
                  ""
                ) : (
                  <LoadingButton
                    loading={loading}
                    loadingIndicator="Sending…"
                    variant="outlined"
                    type="button"
                    onClick={clickDelete}
                  >
                    {formData.category === "P" ? "게시글" : "카테고리"} 삭제
                  </LoadingButton>
                )}

                <LoadingButton
                  loading={loading}
                  loadingIndicator="Sending…"
                  variant="outlined"
                  type="submit"
                  disabled={
                    (formData.category === "P" &&
                      (formData.content === "" || formData.content === null)) ||
                    formData.title === "" ||
                    formData.title === null
                  }
                >
                  작성 완료
                </LoadingButton>
              </Stack>
            </Grid>
          </Grid>
        </form>
      </div>
    </>
  );
};

export default PostEdit;
